import { useState, useRef, TouchEvent, useCallback, useEffect } from 'react';
import { useParams, Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Search, Filter, Grid, List, PackagePlus, ChevronRight, Loader2, Trash2, Calendar, MapPin, ArrowRightLeft, X } from 'lucide-react';
import { api } from '../services/api';
import { ITEM_CATEGORIES, type ItemCategory } from '@findmystuff/shared';
import { useAutoGenerateImages } from '../hooks/useAutoGenerateImages';
import { Pagination } from '../components/common/Pagination';
import clsx from 'clsx';

// Transfer confirmation modal
function TransferModal({
  isOpen,
  onClose,
  onConfirm,
  itemTitle,
  inventories,
  currentInventoryId,
  isTransferring,
}: {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: (inventoryId: string) => void;
  itemTitle: string;
  inventories: Array<{ id: string; name: string }>;
  currentInventoryId: string;
  isTransferring: boolean;
}) {
  const [selectedInventoryId, setSelectedInventoryId] = useState('');

  if (!isOpen) return null;

  const availableInventories = inventories.filter(inv => inv.id !== currentInventoryId);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50" onClick={onClose}>
      <div className="bg-white rounded-xl shadow-xl max-w-md w-full p-6" onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-gray-900">Transfer Item</h3>
          <button onClick={onClose} className="p-1 text-gray-400 hover:text-gray-600">
            <X className="w-5 h-5" />
          </button>
        </div>

        <p className="text-sm text-gray-600 mb-4">
          Transfer "<span className="font-medium">{itemTitle}</span>" to another inventory:
        </p>

        {availableInventories.length === 0 ? (
          <p className="text-sm text-gray-500 italic">No other inventories available. Create another inventory first.</p>
        ) : (
          <select
            value={selectedInventoryId}
            onChange={(e) => setSelectedInventoryId(e.target.value)}
            className="input w-full mb-4"
          >
            <option value="">Select inventory...</option>
            {availableInventories.map((inv) => (
              <option key={inv.id} value={inv.id}>
                {inv.name}
              </option>
            ))}
          </select>
        )}

        <div className="flex gap-3 justify-end">
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200"
          >
            Cancel
          </button>
          <button
            onClick={() => selectedInventoryId && onConfirm(selectedInventoryId)}
            disabled={!selectedInventoryId || isTransferring}
            className="px-4 py-2 text-sm font-medium text-white bg-primary-600 rounded-lg hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
          >
            {isTransferring ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin" />
                Transferring...
              </>
            ) : (
              <>
                <ArrowRightLeft className="w-4 h-4" />
                Transfer
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
}

// Change location modal
function ChangeLocationModal({
  isOpen,
  onClose,
  onConfirm,
  itemTitle,
  currentLocation,
  savedLocations,
  isUpdating,
}: {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: (location: string) => void;
  itemTitle: string;
  currentLocation: string | null;
  savedLocations: Array<{ id: string; name: string; imageUrl?: string }>;
  isUpdating: boolean;
}) {
  const [selectedLocationId, setSelectedLocationId] = useState<string | null>(null);
  const [newLocationName, setNewLocationName] = useState('');
  const [mode, setMode] = useState<'select' | 'new'>('select');

  // Initialize selection when modal opens
  const initializeSelection = useCallback(() => {
    if (currentLocation && savedLocations.length > 0) {
      const matching = savedLocations.find(
        loc => loc.name.toLowerCase() === currentLocation.toLowerCase()
      );
      if (matching) {
        setSelectedLocationId(matching.id);
        setMode('select');
      } else {
        // Current location doesn't match any saved location
        setSelectedLocationId(null);
        setNewLocationName(currentLocation);
        setMode('new');
      }
    } else {
      setSelectedLocationId(null);
      setMode('select');
    }
  }, [currentLocation, savedLocations]);

  // Reset state when modal opens
  useEffect(() => {
    if (isOpen) {
      initializeSelection();
    }
  }, [isOpen, initializeSelection]);

  if (!isOpen) return null;

  const handleSelectChange = (value: string) => {
    if (value === '__new__') {
      setMode('new');
      setSelectedLocationId(null);
      setNewLocationName('');
    } else if (value === '') {
      setSelectedLocationId(null);
      setMode('select');
    } else {
      setSelectedLocationId(value);
      setMode('select');
    }
  };

  const handleConfirm = () => {
    if (mode === 'new' && newLocationName.trim()) {
      onConfirm(newLocationName.trim());
    } else if (mode === 'select' && selectedLocationId) {
      const location = savedLocations.find(loc => loc.id === selectedLocationId);
      if (location) {
        onConfirm(location.name);
      }
    }
  };

  const selectedLocation = savedLocations.find(loc => loc.id === selectedLocationId);
  const canConfirm = mode === 'new' ? !!newLocationName.trim() : !!selectedLocationId;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50" onClick={onClose}>
      <div className="bg-white rounded-xl shadow-xl max-w-md w-full p-6" onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-gray-900">Change Location</h3>
          <button onClick={onClose} className="p-1 text-gray-400 hover:text-gray-600">
            <X className="w-5 h-5" />
          </button>
        </div>

        <p className="text-sm text-gray-600 mb-4">
          Change location for "<span className="font-medium">{itemTitle}</span>"
        </p>

        {/* Location dropdown */}
        <select
          value={mode === 'new' ? '__new__' : (selectedLocationId || '')}
          onChange={(e) => handleSelectChange(e.target.value)}
          className="input w-full mb-3"
        >
          <option value="">No location</option>
          {savedLocations.map((loc) => (
            <option key={loc.id} value={loc.id}>
              {loc.name}
            </option>
          ))}
          <option value="__new__">+ Add new location...</option>
        </select>

        {/* Show selected location preview */}
        {mode === 'select' && selectedLocation && (
          <div className="mb-4 p-3 bg-gray-50 rounded-lg">
            <div className="flex items-center gap-3">
              {selectedLocation.imageUrl ? (
                <img
                  src={selectedLocation.imageUrl}
                  alt="Location"
                  className="w-12 h-12 rounded-lg object-cover"
                />
              ) : (
                <div className="w-12 h-12 bg-gray-200 rounded-lg flex items-center justify-center">
                  <MapPin className="w-6 h-6 text-gray-400" />
                </div>
              )}
              <div>
                <p className="text-sm font-medium text-gray-900">{selectedLocation.name}</p>
                <p className="text-xs text-gray-500">Saved location</p>
              </div>
            </div>
          </div>
        )}

        {/* New location input */}
        {mode === 'new' && (
          <div className="mb-4">
            <input
              type="text"
              value={newLocationName}
              onChange={(e) => setNewLocationName(e.target.value)}
              placeholder="Enter new location name..."
              className="input w-full"
              autoFocus
            />
            <p className="text-xs text-gray-500 mt-1">This will create a new saved location</p>
          </div>
        )}

        <div className="flex gap-3 justify-end">
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200"
          >
            Cancel
          </button>
          <button
            onClick={handleConfirm}
            disabled={isUpdating || !canConfirm}
            className="px-4 py-2 text-sm font-medium text-white bg-primary-600 rounded-lg hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
          >
            {isUpdating ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin" />
                Updating...
              </>
            ) : (
              <>
                <MapPin className="w-4 h-4" />
                Update
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
}

const PAGE_SIZE = 20;

// Format date to human-readable format with time
// Uses calendar day comparison, not time-based difference
const formatDate = (date: string | Date) => {
  const d = new Date(date);
  const now = new Date();

  // Compare calendar dates (midnight-to-midnight), not timestamps
  const dateOnly = new Date(d.getFullYear(), d.getMonth(), d.getDate());
  const todayOnly = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const diffDays = Math.round((todayOnly.getTime() - dateOnly.getTime()) / (1000 * 60 * 60 * 24));

  const time = d.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: false });

  if (diffDays === 0) {
    return `Today at ${time}`;
  } else if (diffDays === 1) {
    return `Yesterday at ${time}`;
  } else if (diffDays < 7) {
    return `${diffDays} days ago at ${time}`;
  } else {
    return `${d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })} at ${time}`;
  }
};

// Swipeable item component for mobile
function SwipeableItem({
  item,
  onDelete,
  onTransfer,
  onChangeLocation,
  isDeleting,
  isGenerating
}: {
  item: any;
  onDelete: () => void;
  onTransfer: () => void;
  onChangeLocation: () => void;
  isDeleting: boolean;
  isGenerating: boolean;
}) {
  const navigate = useNavigate();
  const [swipeOffset, setSwipeOffset] = useState(0);
  const [isSwipeOpen, setIsSwipeOpen] = useState(false);
  const touchStartX = useRef(0);
  const touchStartY = useRef(0);
  const isHorizontalSwipe = useRef(false);

  const SWIPE_WIDTH = 180; // Width for three buttons

  const handleTouchStart = (e: TouchEvent) => {
    const touch = e.touches[0];
    if (!touch) return;
    touchStartX.current = touch.clientX;
    touchStartY.current = touch.clientY;
    isHorizontalSwipe.current = false;
  };

  const handleTouchMove = (e: TouchEvent) => {
    const touch = e.touches[0];
    if (!touch) return;
    const deltaX = touch.clientX - touchStartX.current;
    const deltaY = touch.clientY - touchStartY.current;

    // Determine if this is a horizontal swipe
    if (!isHorizontalSwipe.current && Math.abs(deltaX) > 10) {
      isHorizontalSwipe.current = Math.abs(deltaX) > Math.abs(deltaY);
    }

    if (isHorizontalSwipe.current) {
      e.preventDefault();
      // Only allow swipe left (negative) or back to center
      const newOffset = isSwipeOpen
        ? Math.min(0, Math.max(-SWIPE_WIDTH, deltaX - SWIPE_WIDTH))
        : Math.min(0, Math.max(-SWIPE_WIDTH, deltaX));
      setSwipeOffset(newOffset);
    }
  };

  const handleTouchEnd = () => {
    if (swipeOffset < -70) {
      setSwipeOffset(-SWIPE_WIDTH);
      setIsSwipeOpen(true);
    } else {
      setSwipeOffset(0);
      setIsSwipeOpen(false);
    }
  };

  const handleItemClick = () => {
    if (isSwipeOpen) {
      setSwipeOffset(0);
      setIsSwipeOpen(false);
    } else if (Math.abs(swipeOffset) < 10) {
      navigate(`/items/${item.id}`);
    }
  };

  return (
    <div className="relative overflow-hidden">
      {/* Action buttons behind */}
      <div className="absolute right-0 top-0 bottom-0 flex" style={{ width: SWIPE_WIDTH }}>
        <button
          onClick={onChangeLocation}
          className="w-[60px] h-full flex items-center justify-center text-white bg-green-500"
        >
          <MapPin className="w-5 h-5" />
        </button>
        <button
          onClick={onTransfer}
          className="w-[60px] h-full flex items-center justify-center text-white bg-blue-500"
        >
          <ArrowRightLeft className="w-5 h-5" />
        </button>
        <button
          onClick={onDelete}
          disabled={isDeleting}
          className="w-[60px] h-full flex items-center justify-center text-white bg-red-500"
        >
          {isDeleting ? (
            <Loader2 className="w-6 h-6 animate-spin" />
          ) : (
            <Trash2 className="w-6 h-6" />
          )}
        </button>
      </div>

      {/* Item content */}
      <div
        className="relative bg-white p-4 flex items-center gap-4 transition-transform"
        style={{ transform: `translateX(${swipeOffset}px)` }}
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
        onClick={handleItemClick}
      >
        <div className="w-16 h-16 bg-gray-100 rounded-lg flex items-center justify-center flex-shrink-0 overflow-hidden">
          {item.images?.[0] ? (
            <img
              src={item.images[0].thumbnailUrl || item.images[0].url}
              alt={item.title}
              loading="lazy"
              className="w-full h-full object-cover rounded-lg"
            />
          ) : isGenerating ? (
            <Loader2 className="w-6 h-6 animate-spin text-gray-400" />
          ) : (
            <span className="text-2xl text-gray-300">📦</span>
          )}
        </div>
        <div className="flex-1 min-w-0">
          <h3 className="font-medium text-gray-900">
            {item.title}
          </h3>
          <p className="text-sm text-gray-500 line-clamp-1">
            {item.description}
          </p>
          <div className="flex items-center gap-2 mt-1 flex-wrap">
            <span className="text-xs bg-gray-100 text-gray-600 px-2 py-0.5 rounded">
              {item.category}
            </span>
            {item.location && (
              <span className="text-xs text-gray-500 inline-flex items-center gap-1">
                <MapPin className="w-3 h-3" />
                {item.location}
                {item.shippedAt ? (
                  <span className="bg-amber-100 text-amber-700 px-1.5 py-0.5 rounded text-[10px] font-medium">Shipped</span>
                ) : item.locationPosition && (
                  <span className="bg-emerald-100 text-emerald-700 px-1.5 py-0.5 rounded text-[10px] font-medium">#{item.locationPosition}</span>
                )}
              </span>
            )}
            <span className="text-xs text-gray-400 inline-flex items-center gap-1">
              <Calendar className="w-3 h-3" />
              {formatDate(item.createdAt)}
            </span>
            {item.needsReview && (
              <span className="text-xs bg-yellow-100 text-yellow-800 px-2 py-0.5 rounded">
                Needs Review
              </span>
            )}
          </div>
        </div>
        <ChevronRight className="w-5 h-5 text-gray-400 flex-shrink-0" />
      </div>
    </div>
  );
}

export function InventoryPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [deletingIds, setDeletingIds] = useState<Set<string>>(new Set());
  const [transferItem, setTransferItem] = useState<{ id: string; title: string } | null>(null);
  const queryClient = useQueryClient();

  // Fetch all inventories for transfer modal
  const { data: allInventories } = useQuery({
    queryKey: ['inventories'],
    queryFn: () => api.inventories.list(),
  });

  const transferItemMutation = useMutation({
    mutationFn: ({ itemId, inventoryId }: { itemId: string; inventoryId: string }) =>
      api.items.transfer(itemId, inventoryId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['items'] });
      queryClient.invalidateQueries({ queryKey: ['inventories'] });
      setTransferItem(null);
    },
  });

  const handleTransferItem = (e: React.MouseEvent, itemId: string, itemTitle: string) => {
    e.preventDefault();
    e.stopPropagation();
    setTransferItem({ id: itemId, title: itemTitle });
  };

  const confirmTransfer = (inventoryId: string) => {
    if (transferItem) {
      transferItemMutation.mutate({ itemId: transferItem.id, inventoryId });
    }
  };

  // Location change state and mutation
  const [locationChangeItem, setLocationChangeItem] = useState<{ id: string; title: string; currentLocation: string | null } | null>(null);

  // Fetch saved locations for the location modal
  const { data: savedLocations = [] } = useQuery({
    queryKey: ['saved-locations'],
    queryFn: () => api.locations.list(),
  });

  const changeLocationMutation = useMutation({
    mutationFn: async ({ itemId, location }: { itemId: string; location: string }) => {
      // Check if location exists in saved locations, if not create it
      const existingLocation = savedLocations.find(
        loc => loc.name.toLowerCase() === location.toLowerCase()
      );
      if (!existingLocation) {
        await api.locations.create(location);
      }
      return api.items.update(itemId, { location });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['items'] });
      queryClient.invalidateQueries({ queryKey: ['item-locations'] });
      queryClient.invalidateQueries({ queryKey: ['saved-locations'] });
      setLocationChangeItem(null);
    },
  });

  const handleChangeLocation = (e: React.MouseEvent, itemId: string, itemTitle: string, currentLocation: string | null) => {
    e.preventDefault();
    e.stopPropagation();
    setLocationChangeItem({ id: itemId, title: itemTitle, currentLocation });
  };

  const confirmLocationChange = (location: string) => {
    if (locationChangeItem) {
      changeLocationMutation.mutate({ itemId: locationChangeItem.id, location });
    }
  };

  // Get all filter state from URL params
  const page = parseInt(searchParams.get('page') || '1', 10);
  const viewMode = (searchParams.get('view') || 'grid') as 'grid' | 'list';
  const search = searchParams.get('search') || '';
  const category = (searchParams.get('category') || '') as ItemCategory | '';
  const location = searchParams.get('location') || '';

  // Helper to update URL params while preserving others
  const updateParams = useCallback((updates: Record<string, string | null>) => {
    setSearchParams((prev) => {
      const params = new URLSearchParams(prev);
      Object.entries(updates).forEach(([key, value]) => {
        if (value === null || value === '' || (key === 'page' && value === '1')) {
          params.delete(key);
        } else {
          params.set(key, value);
        }
      });
      return params;
    });
  }, [setSearchParams]);

  // Update page in URL
  const setPage = useCallback((newPage: number) => {
    updateParams({ page: String(newPage) });
  }, [updateParams]);

  // Update view mode in URL
  const setViewMode = useCallback((mode: 'grid' | 'list') => {
    updateParams({ view: mode === 'grid' ? null : mode });
  }, [updateParams]);

  const deleteItemMutation = useMutation({
    mutationFn: (itemId: string) => api.items.delete(itemId),
    onMutate: async (itemId) => {
      // Track that this item is being deleted
      setDeletingIds(prev => new Set(prev).add(itemId));

      // Cancel any outgoing refetches
      await queryClient.cancelQueries({ queryKey: ['items'] });

      // Snapshot previous value (must match query key exactly including page)
      const previousItems = queryClient.getQueryData(['items', { inventoryId: id, search, category, location, page }]);

      // Optimistically remove the item
      queryClient.setQueryData(['items', { inventoryId: id, search, category, location, page }], (old: any) => {
        if (!old) return old;
        return {
          ...old,
          items: old.items.filter((item: any) => item.id !== itemId),
        };
      });

      return { previousItems };
    },
    onError: (_err, itemId, context) => {
      // Roll back on error
      if (context?.previousItems) {
        queryClient.setQueryData(['items', { inventoryId: id, search, category, location, page }], context.previousItems);
      }
      setDeletingIds(prev => {
        const next = new Set(prev);
        next.delete(itemId);
        return next;
      });
    },
    onSettled: (_, __, itemId) => {
      setDeletingIds(prev => {
        const next = new Set(prev);
        next.delete(itemId);
        return next;
      });
      queryClient.invalidateQueries({ queryKey: ['items'] });
      queryClient.invalidateQueries({ queryKey: ['inventories'] });
      queryClient.invalidateQueries({ queryKey: ['item-locations'] });
    },
  });

  const handleDeleteItem = (e: React.MouseEvent | null, itemId: string, skipConfirmation = false) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }

    // Prevent double clicks
    if (deletingIds.has(itemId) || deleteItemMutation.isPending) {
      return;
    }

    // Show confirmation for click-based deletes (not swipe)
    if (!skipConfirmation && !confirm('Are you sure you want to delete this item?')) {
      return;
    }

    deleteItemMutation.mutate(itemId);
  };

  // Delete inventory mutation
  const [isDeletingInventory, setIsDeletingInventory] = useState(false);
  const deleteInventoryMutation = useMutation({
    mutationFn: (inventoryId: string) => api.inventories.delete(inventoryId),
    onMutate: () => setIsDeletingInventory(true),
    onSettled: () => setIsDeletingInventory(false),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['inventories'] });
      navigate('/');
    },
  });

  const handleDeleteInventory = () => {
    if (!inventory) return;

    if (inventory.itemCount > 0) {
      alert('Cannot delete inventory with items. Remove all items first.');
      return;
    }

    if (confirm(`Are you sure you want to delete "${inventory.name}"?`)) {
      deleteInventoryMutation.mutate(inventory.id);
    }
  };

  const { data: inventory } = useQuery({
    queryKey: ['inventory', id],
    queryFn: () => api.inventories.get(id!),
    enabled: !!id,
  });

  // Fetch unique locations for this inventory
  const { data: locations } = useQuery({
    queryKey: ['item-locations', id],
    queryFn: () => api.items.getLocations(id),
    enabled: !!id,
  });

  const { data: itemsData, isLoading } = useQuery({
    queryKey: ['items', { inventoryId: id, search, category, location, page }],
    queryFn: () =>
      api.items.list({
        ...(id && { inventoryId: id }),
        ...(search && { search }),
        ...(category && { category }),
        ...(location && { location }),
        page,
        pageSize: PAGE_SIZE,
      }),
    enabled: !!id,
  });

  const items = itemsData?.items || [];
  const totalPages = itemsData?.meta?.totalPages || 1;
  const totalCount = itemsData?.meta?.totalCount || 0;

  // Reset to page 1 when filters change
  const handleSearchChange = useCallback((value: string) => {
    updateParams({ search: value, page: '1' });
  }, [updateParams]);

  const handleCategoryChange = useCallback((value: ItemCategory | '') => {
    updateParams({ category: value, page: '1' });
  }, [updateParams]);

  const handleLocationChange = useCallback((value: string) => {
    updateParams({ location: value, page: '1' });
  }, [updateParams]);

  // Auto-generate images for items without them
  const { isGenerating } = useAutoGenerateImages(items as any);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">
            {inventory?.name || 'Inventory'}
          </h1>
          {inventory?.description && (
            <p className="text-gray-600">{inventory.description}</p>
          )}
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={handleDeleteInventory}
            disabled={isDeletingInventory || (inventory?.itemCount ?? 0) > 0}
            className={`px-3 py-2 rounded-lg transition-colors flex items-center gap-2 text-sm font-medium ${
              (inventory?.itemCount ?? 0) === 0
                ? 'bg-red-100 text-red-600 hover:bg-red-200'
                : 'bg-gray-100 text-gray-400 cursor-not-allowed'
            }`}
            title={(inventory?.itemCount ?? 0) > 0 ? 'Remove all items before deleting' : 'Delete inventory'}
          >
            {isDeletingInventory ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : (
              <Trash2 className="w-4 h-4" />
            )}
            Delete Inventory
          </button>
          <Link to={`/add-inventory?inventoryId=${id}`} className="btn-primary">
            <PackagePlus className="w-4 h-4 mr-2" />
            Add Items
          </Link>
        </div>
      </div>

      {/* Filters */}
      <div className="card p-4">
        <div className="flex flex-col sm:flex-row gap-4">
          {/* Search */}
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <input
              type="text"
              placeholder="Search items..."
              value={search}
              onChange={(e) => handleSearchChange(e.target.value)}
              className="input pl-10"
            />
          </div>

          {/* Category filter */}
          <div className="relative">
            <Filter className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <select
              value={category}
              onChange={(e) => handleCategoryChange(e.target.value as ItemCategory | '')}
              className="input pl-10 pr-8 appearance-none"
            >
              <option value="">All Categories</option>
              {ITEM_CATEGORIES.map((cat) => (
                <option key={cat} value={cat}>
                  {cat}
                </option>
              ))}
            </select>
          </div>

          {/* Location filter */}
          {locations && locations.length > 0 && (
            <div className="relative">
              <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <select
                value={location}
                onChange={(e) => handleLocationChange(e.target.value)}
                className="input pl-10 pr-8 appearance-none"
              >
                <option value="">All Locations</option>
                {locations.map((loc) => (
                  <option key={loc} value={loc}>
                    {loc}
                  </option>
                ))}
              </select>
            </div>
          )}

          {/* View toggle */}
          <div className="flex border border-gray-300 rounded-lg overflow-hidden">
            <button
              onClick={() => setViewMode('grid')}
              className={clsx(
                'p-2',
                viewMode === 'grid'
                  ? 'bg-primary-600 text-white'
                  : 'bg-white text-gray-600 hover:bg-gray-50'
              )}
            >
              <Grid className="w-4 h-4" />
            </button>
            <button
              onClick={() => setViewMode('list')}
              className={clsx(
                'p-2',
                viewMode === 'list'
                  ? 'bg-primary-600 text-white'
                  : 'bg-white text-gray-600 hover:bg-gray-50'
              )}
            >
              <List className="w-4 h-4" />
            </button>
          </div>
        </div>
      </div>

      {/* Items */}
      {isLoading ? (
        <div className="card p-8 text-center text-gray-500">Loading items...</div>
      ) : items.length === 0 ? (
        <div className="card p-8 text-center">
          <p className="text-gray-500 mb-4">No items found</p>
          <Link to={`/add-inventory?inventoryId=${id}`} className="btn-primary">
            <PackagePlus className="w-4 h-4 mr-2" />
            Start Adding Items
          </Link>
        </div>
      ) : (
        <>
          {/* Mobile: Swipeable list (always shown on small screens) */}
          <div className="sm:hidden card divide-y divide-gray-100">
            {items.map((item) => (
              <SwipeableItem
                key={item.id}
                item={item}
                onDelete={() => handleDeleteItem(null, item.id, true)}
                onTransfer={() => setTransferItem({ id: item.id, title: item.title })}
                onChangeLocation={() => setLocationChangeItem({ id: item.id, title: item.title, currentLocation: item.location || null })}
                isDeleting={deletingIds.has(item.id)}
                isGenerating={isGenerating}
              />
            ))}
            <p className="text-xs text-gray-400 text-center py-2">Swipe left for options</p>
          </div>

          {/* Desktop: Grid or List view */}
          <div className="hidden sm:block">
            {viewMode === 'grid' ? (
              <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
                {items.map((item) => (
                  <Link
                    key={item.id}
                    to={`/items/${item.id}`}
                    className="card p-4 hover:border-primary-300 transition-colors group"
                  >
                    <div className="aspect-square bg-gray-100 rounded-lg mb-3 flex items-center justify-center overflow-hidden relative">
                      {item.images?.[0] ? (
                        <img
                          src={item.images[0].thumbnailUrl || item.images[0].url}
                          alt={item.title}
                          loading="lazy"
                          className="w-full h-full object-cover rounded-lg"
                        />
                      ) : isGenerating ? (
                        <div className="flex flex-col items-center text-gray-400">
                          <Loader2 className="w-8 h-8 animate-spin" />
                          <span className="text-xs mt-1">Generating...</span>
                        </div>
                      ) : (
                        <span className="text-4xl text-gray-300">📦</span>
                      )}
                      {/* Action buttons - always visible */}
                      <div className="absolute bottom-2 right-2 flex gap-1">
                        <button
                          onClick={(e) => handleChangeLocation(e, item.id, item.title, item.location || null)}
                          className="p-2 bg-green-500 text-white rounded-lg hover:bg-green-600 transition-colors shadow-lg"
                          title="Change location"
                        >
                          <MapPin className="w-4 h-4" />
                        </button>
                        <button
                          onClick={(e) => handleTransferItem(e, item.id, item.title)}
                          className="p-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors shadow-lg"
                          title="Transfer to another inventory"
                        >
                          <ArrowRightLeft className="w-4 h-4" />
                        </button>
                        <button
                          onClick={(e) => handleDeleteItem(e, item.id)}
                          disabled={deletingIds.has(item.id)}
                          className="p-2 bg-red-500 text-white rounded-lg hover:bg-red-600 transition-colors disabled:opacity-50 shadow-lg"
                          title="Delete item"
                        >
                          {deletingIds.has(item.id) ? (
                            <Loader2 className="w-4 h-4 animate-spin" />
                          ) : (
                            <Trash2 className="w-4 h-4" />
                          )}
                        </button>
                      </div>
                    </div>
                    <h3 className="font-medium text-gray-900 group-hover:text-primary-600 line-clamp-1">
                      {item.title}
                    </h3>
                    <div className="flex items-center gap-2 text-sm text-gray-500 flex-wrap">
                      <span>{item.category}</span>
                      {item.location && (
                        <>
                          <span className="text-gray-300">•</span>
                          <span className="inline-flex items-center gap-1">
                            <MapPin className="w-3 h-3" />
                            {item.location}
                            {item.shippedAt ? (
                              <span className="bg-amber-100 text-amber-700 px-1.5 py-0.5 rounded text-[10px] font-medium ml-1">Shipped</span>
                            ) : item.locationPosition && (
                              <span className="bg-emerald-100 text-emerald-700 px-1.5 py-0.5 rounded text-[10px] font-medium ml-1">#{item.locationPosition}</span>
                            )}
                          </span>
                        </>
                      )}
                      <span className="text-gray-300">•</span>
                      <span className="inline-flex items-center gap-1">
                        <Calendar className="w-3 h-3" />
                        {formatDate(item.createdAt)}
                      </span>
                    </div>
                    {item.needsReview && (
                      <span className="mt-2 inline-block text-xs bg-yellow-100 text-yellow-800 px-2 py-1 rounded">
                        Needs Review
                      </span>
                    )}
                  </Link>
                ))}
              </div>
            ) : (
              <div className="card divide-y divide-gray-100">
                {items.map((item) => (
                  <Link
                    key={item.id}
                    to={`/items/${item.id}`}
                    className="p-4 flex items-center gap-4 hover:bg-gray-50 group"
                  >
                    <div className="w-16 h-16 bg-gray-100 rounded-lg flex items-center justify-center flex-shrink-0 overflow-hidden">
                      {item.images?.[0] ? (
                        <img
                          src={item.images[0].thumbnailUrl || item.images[0].url}
                          alt={item.title}
                          loading="lazy"
                          className="w-full h-full object-cover rounded-lg"
                        />
                      ) : isGenerating ? (
                        <Loader2 className="w-6 h-6 animate-spin text-gray-400" />
                      ) : (
                        <span className="text-2xl text-gray-300">📦</span>
                      )}
                    </div>
                    <div className="flex-1 min-w-0">
                      <h3 className="font-medium text-gray-900 group-hover:text-primary-600">
                        {item.title}
                      </h3>
                      <p className="text-sm text-gray-500 line-clamp-1">
                        {item.description}
                      </p>
                      <div className="flex items-center gap-2 mt-1 flex-wrap">
                        <span className="text-xs bg-gray-100 text-gray-600 px-2 py-0.5 rounded">
                          {item.category}
                        </span>
                        {item.location && (
                          <span className="text-xs text-gray-500 inline-flex items-center gap-1">
                            <MapPin className="w-3 h-3" />
                            {item.location}
                            {item.shippedAt ? (
                              <span className="bg-amber-100 text-amber-700 px-1.5 py-0.5 rounded text-[10px] font-medium ml-1">Shipped</span>
                            ) : item.locationPosition && (
                              <span className="bg-emerald-100 text-emerald-700 px-1.5 py-0.5 rounded text-[10px] font-medium ml-1">#{item.locationPosition}</span>
                            )}
                          </span>
                        )}
                        <span className="text-xs text-gray-400 inline-flex items-center gap-1">
                          <Calendar className="w-3 h-3" />
                          {formatDate(item.createdAt)}
                        </span>
                        {item.needsReview && (
                          <span className="text-xs bg-yellow-100 text-yellow-800 px-2 py-0.5 rounded">
                            Needs Review
                          </span>
                        )}
                      </div>
                    </div>
                    <button
                      onClick={(e) => handleChangeLocation(e, item.id, item.title, item.location || null)}
                      className="p-2 bg-green-100 text-green-600 rounded-lg hover:bg-green-200 transition-colors"
                      title="Change location"
                    >
                      <MapPin className="w-5 h-5" />
                    </button>
                    <button
                      onClick={(e) => handleTransferItem(e, item.id, item.title)}
                      className="p-2 bg-blue-100 text-blue-600 rounded-lg hover:bg-blue-200 transition-colors"
                      title="Transfer to another inventory"
                    >
                      <ArrowRightLeft className="w-5 h-5" />
                    </button>
                    <button
                      onClick={(e) => handleDeleteItem(e, item.id)}
                      disabled={deletingIds.has(item.id)}
                      className="p-2 bg-red-100 text-red-600 rounded-lg hover:bg-red-200 transition-colors disabled:opacity-50"
                      title="Delete item"
                    >
                      {deletingIds.has(item.id) ? (
                        <Loader2 className="w-5 h-5 animate-spin" />
                      ) : (
                        <Trash2 className="w-5 h-5" />
                      )}
                    </button>
                  </Link>
                ))}
              </div>
            )}
          </div>
        </>
      )}

      {/* Pagination */}
      <Pagination
        currentPage={page}
        totalPages={totalPages}
        totalCount={totalCount}
        pageSize={PAGE_SIZE}
        onPageChange={setPage}
      />

      {/* Transfer Modal */}
      <TransferModal
        isOpen={!!transferItem}
        onClose={() => setTransferItem(null)}
        onConfirm={confirmTransfer}
        itemTitle={transferItem?.title || ''}
        inventories={allInventories || []}
        currentInventoryId={id || ''}
        isTransferring={transferItemMutation.isPending}
      />

      {/* Change Location Modal */}
      <ChangeLocationModal
        isOpen={!!locationChangeItem}
        onClose={() => setLocationChangeItem(null)}
        onConfirm={confirmLocationChange}
        itemTitle={locationChangeItem?.title || ''}
        currentLocation={locationChangeItem?.currentLocation || null}
        savedLocations={savedLocations}
        isUpdating={changeLocationMutation.isPending}
      />
    </div>
  );
}
